import { useMemo, useState } from "react";
import { DetailHeader } from "@/components/layout/detail-header";
import { Button } from "@/components/ui/button";
import { useToast } from "@/contexts/toast-context";
import { Combobox } from "@/components/ui/combobox";
import { Loader2, Search } from "lucide-react";

type ServerHost = "adp.flatfile.com" | "qa.adp.flatfile.com";

interface JobItem {
  id: string;
  type?: string;
  operation?: string;
  status?: string;
  progress?: number;
  info?: string;
  createdAt?: string;
  updatedAt?: string;
  startedAt?: string;
  finishedAt?: string;
  source?: string;
  mode?: string;
  outcome?: Record<string, unknown>;
}

export default function JobFinderPage() {
  const { showError, showSuccess } = useToast();
  
  const [server, setServer] = useState<ServerHost>("adp.flatfile.com");
  const [token, setToken] = useState("");
  const [spaceId, setSpaceId] = useState("");
  const [isLoadingJobs, setIsLoadingJobs] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState("");
  const [jobs, setJobs] = useState<JobItem[]>([]);
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 20;

  const apiBase = useMemo(() => `https://${server}/api/v1`, [server]);

  function authHeader(tokenValue: string) {
    return tokenValue.startsWith("Bearer ") ? tokenValue : `Bearer ${tokenValue}`;
  }

  async function fetchJson(url: string) {
    const res = await fetch(url, {
      headers: {
        "Content-Type": "application/json",
        "Authorization": authHeader(token),
        "X-Disable-Hooks": "true",
      },
    });
    if (!res.ok) {
      const text = await res.text().catch(() => "");
      throw new Error(`HTTP ${res.status} ${res.statusText}${text ? `: ${text}` : ""}`);
    }
    return res.json();
  }

  async function loadAllJobs() {
    if (!spaceId || !token) {
      showError("Please enter a space ID and token.");
      return;
    }
    setIsLoadingJobs(true);
    setJobs([]);
    setSelectedJobId(null);
    setCurrentPage(1);
    setSearchTerm("");

    const allJobs: JobItem[] = [];
    let pageNumber = 1;
    const fetchPageSize = 100; // Fetch in larger batches for efficiency

    try {
      while (true) {
        setLoadingProgress(`Loading page ${pageNumber}...`);
        const url = `${apiBase}/jobs?spaceId=${encodeURIComponent(spaceId)}&pageSize=${fetchPageSize}&pageNumber=${pageNumber}&sortDirection=desc`;
        const data = await fetchJson(url);
        const batch: JobItem[] = Array.isArray(data?.data) ? data.data : [];
        
        if (batch.length === 0) break;
        
        allJobs.push(...batch);
        setLoadingProgress(`Loaded ${allJobs.length} jobs...`);
        
        // Check if we've reached the last page
        const totalPages = data?.pagination?.pageCount || 1;
        if (pageNumber >= totalPages) break;
        
        pageNumber++;
      }

      setJobs(allJobs);
      
      if (allJobs.length === 0) {
        showSuccess("No jobs found for this space.");
      } else {
        showSuccess(`Loaded ${allJobs.length} job(s).`);
      }
    } catch (e: any) {
      showError("Couldn't load jobs. Please check the server, space ID, and token.", "Failed to load jobs");
      setJobs([]);
    } finally {
      setIsLoadingJobs(false);
      setLoadingProgress("");
    }
  }

  function getStatusColor(status?: string) {
    switch (status) {
      case "complete": return "text-green-500";
      case "failed": return "text-red-500";
      case "executing": return "text-blue-500";
      case "canceled": return "text-orange-500";
      case "waiting": return "text-yellow-500";
      default: return "text-muted-foreground";
    }
  }

  // Filter jobs based on search term (client-side)
  const filteredJobs = useMemo(() => {
    if (!searchTerm.trim()) return jobs;
    const q = searchTerm.toLowerCase();
    return jobs.filter(job => 
      job.id?.toLowerCase().includes(q) ||
      job.operation?.toLowerCase().includes(q) ||
      job.status?.toLowerCase().includes(q) ||
      job.type?.toLowerCase().includes(q) ||
      job.source?.toLowerCase().includes(q) ||
      job.info?.toLowerCase().includes(q)
    );
  }, [jobs, searchTerm]);

  // Client-side pagination
  const totalPages = Math.max(1, Math.ceil(filteredJobs.length / pageSize));
  const paginatedJobs = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredJobs.slice(start, start + pageSize);
  }, [filteredJobs, currentPage]);

  // Reset to page 1 when search changes
  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const selectedJob = useMemo(() => {
    return jobs.find(j => j.id === selectedJobId) || null;
  }, [jobs, selectedJobId]);

  return (
    <div className="flex flex-col h-full">
      <DetailHeader
        breadcrumb={[{ label: "Job Finder" }]}
      />

      <div className="flex-1 overflow-hidden min-h-0">
        <div className="flex h-full min-h-0">
          {/* Left panel / sub-sidebar */}
          <aside className="w-80 min-w-80 max-w-80 border-r border-border flex flex-col">
            <div className="p-4 border-b border-border sticky top-0 bg-background z-10 space-y-3">
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">Server</label>
                <Combobox
                  value={server}
                  onChange={(val) => setServer((val as ServerHost) || "adp.flatfile.com")}
                  options={[
                    { value: "adp.flatfile.com", label: "adp.flatfile.com", description: "Production" },
                    { value: "qa.adp.flatfile.com", label: "qa.adp.flatfile.com", description: "QA" },
                  ]}
                  placeholder="Select server..."
                  className="text-xs"
                />
              </div>

              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">Space ID</label>
                <input
                  className="w-full px-3 py-2.5 text-xs border border-border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                  placeholder="adp_sp_..."
                  value={spaceId}
                  onChange={(e) => setSpaceId(e.target.value)}
                  autoComplete="off"
                />
              </div>

              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">Access token</label>
                <input
                  className="w-full px-3 py-2.5 text-xs border border-border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                  placeholder="Bearer ..."
                  type="password"
                  value={token}
                  onChange={(e) => setToken(e.target.value)}
                  autoComplete="off"
                />
              </div>

              <div className="flex gap-2">
                <Button
                  onClick={loadAllJobs}
                  disabled={!spaceId || !token || isLoadingJobs}
                  size="sm"
                  className="text-xs h-9 cursor-pointer"
                >
                  {isLoadingJobs ? "Loading..." : "Load All Jobs"}
                </Button>
              </div>
            </div>

            {/* Search and Jobs List */}
            <div className="flex-1 overflow-y-auto min-h-0 flex flex-col">
              {jobs.length > 0 && (
                <>
                  {/* Search Input */}
                  <div className="p-3 border-b border-border">
                    <div className="relative">
                      <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                      <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => handleSearchChange(e.target.value)}
                        placeholder="Search jobs (ID, operation, status...)"
                        className="w-full pl-8 pr-3 py-1.5 text-xs border border-border rounded bg-background focus:outline-none focus:ring-1 focus:ring-primary"
                        autoComplete="off"
                      />
                    </div>
                  </div>

                  {/* Pagination Info */}
                  <div className="px-3 py-2 border-b border-border flex items-center justify-between text-[11px] text-muted-foreground">
                    <span>
                      {filteredJobs.length === jobs.length 
                        ? `${jobs.length} jobs` 
                        : `${filteredJobs.length} of ${jobs.length} jobs`}
                      {totalPages > 1 && ` • Page ${currentPage}/${totalPages}`}
                    </span>
                    {totalPages > 1 && (
                      <div className="flex items-center gap-1">
                        <Button
                          size="sm"
                          variant="secondary"
                          className="h-6 text-[10px] px-2 cursor-pointer"
                          disabled={currentPage <= 1}
                          onClick={() => setCurrentPage(p => p - 1)}
                        >
                          Prev
                        </Button>
                        <Button
                          size="sm"
                          variant="secondary"
                          className="h-6 text-[10px] px-2 cursor-pointer"
                          disabled={currentPage >= totalPages}
                          onClick={() => setCurrentPage(p => p + 1)}
                        >
                          Next
                        </Button>
                      </div>
                    )}
                  </div>
                </>
              )}

              {isLoadingJobs ? (
                <div className="flex-1 flex flex-col items-center justify-center text-xs text-muted-foreground gap-2">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>{loadingProgress || "Loading..."}</span>
                </div>
              ) : jobs.length === 0 ? (
                <div className="flex-1 flex items-center justify-center text-xs text-muted-foreground p-3">
                  {spaceId && token ? "No jobs loaded" : "Enter space ID and token"}
                </div>
              ) : filteredJobs.length === 0 ? (
                <div className="flex-1 flex items-center justify-center text-xs text-muted-foreground p-3">
                  No jobs match "{searchTerm}"
                </div>
              ) : (
                <div className="divide-y divide-border border-b border-border">
                  {paginatedJobs.map((job) => {
                    const isSelected = selectedJobId === job.id;
                    return (
                      <button
                        key={job.id}
                        className={`w-full text-left p-3 hover:bg-muted/50 cursor-pointer ${isSelected ? "bg-muted" : ""}`}
                        onClick={() => setSelectedJobId(isSelected ? null : job.id)}
                      >
                        <div className="text-xs font-mono truncate">{job.id}</div>
                        <div className="text-[10px] text-muted-foreground flex items-center gap-1.5 mt-0.5">
                          <span>{job.operation || "unknown"}</span>
                          <span>•</span>
                          <span className={getStatusColor(job.status)}>{job.status || "unknown"}</span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </aside>

          {/* Right pane - Job Details */}
          <section className="flex-1 min-h-0 overflow-auto">
            {!selectedJob ? (
              <div className="h-full flex items-center justify-center text-xs text-muted-foreground">
                Select a job to view details
              </div>
            ) : (
              <div className="h-full flex flex-col">
                <div className="flex items-center justify-between pb-3 border-b border-border p-4">
                  <div className="text-sm font-medium font-mono truncate">{selectedJob.id}</div>
                  <div className={`text-xs font-medium ${getStatusColor(selectedJob.status)}`}>
                    {selectedJob.status || "unknown"}
                  </div>
                </div>
                
                <div className="flex-1 overflow-auto p-4">
                  <div className="space-y-4">
                    {/* Basic Info */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-3">
                        <div>
                          <div className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">Type</div>
                          <div className="text-sm font-mono">{selectedJob.type || "-"}</div>
                        </div>
                        <div>
                          <div className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">Operation</div>
                          <div className="text-sm font-mono">{selectedJob.operation || "-"}</div>
                        </div>
                        <div>
                          <div className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">Mode</div>
                          <div className="text-sm font-mono">{selectedJob.mode || "-"}</div>
                        </div>
                      </div>
                      <div className="space-y-3">
                        <div>
                          <div className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">Source</div>
                          <div className="text-sm font-mono truncate">{selectedJob.source || "-"}</div>
                        </div>
                        <div>
                          <div className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">Progress</div>
                          <div className="text-sm font-mono">{selectedJob.progress !== undefined ? `${selectedJob.progress}%` : "-"}</div>
                        </div>
                        <div>
                          <div className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">Status</div>
                          <div className={`text-sm font-mono ${getStatusColor(selectedJob.status)}`}>{selectedJob.status || "-"}</div>
                        </div>
                      </div>
                    </div>

                    {/* Timestamps */}
                    <div className="border-t border-border pt-4">
                      <div className="text-[10px] text-muted-foreground uppercase tracking-wider mb-3">Timestamps</div>
                      <div className="grid grid-cols-2 gap-3 text-xs">
                        <div>
                          <span className="text-muted-foreground">Created:</span>{" "}
                          <span className="font-mono">{selectedJob.createdAt ? new Date(selectedJob.createdAt).toLocaleString() : "-"}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Started:</span>{" "}
                          <span className="font-mono">{selectedJob.startedAt ? new Date(selectedJob.startedAt).toLocaleString() : "-"}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Finished:</span>{" "}
                          <span className="font-mono">{selectedJob.finishedAt ? new Date(selectedJob.finishedAt).toLocaleString() : "-"}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Updated:</span>{" "}
                          <span className="font-mono">{selectedJob.updatedAt ? new Date(selectedJob.updatedAt).toLocaleString() : "-"}</span>
                        </div>
                      </div>
                    </div>

                    {/* Info */}
                    {selectedJob.info && (
                      <div className="border-t border-border pt-4">
                        <div className="text-[10px] text-muted-foreground uppercase tracking-wider mb-2">Info</div>
                        <div className="text-sm font-mono bg-muted/50 p-3 rounded">{selectedJob.info}</div>
                      </div>
                    )}

                    {/* Outcome */}
                    {selectedJob.outcome && Object.keys(selectedJob.outcome).length > 0 && (
                      <div className="border-t border-border pt-4">
                        <div className="text-[10px] text-muted-foreground uppercase tracking-wider mb-2">Outcome</div>
                        <pre className="text-xs font-mono bg-muted/50 p-3 rounded overflow-x-auto">
                          {JSON.stringify(selectedJob.outcome, null, 2)}
                        </pre>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
